// src/components/tour_groups_calendar/content/TourGroupsCalendarContent.tsx
import styled from "styled-components";
import { useMemo, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import SelectionBar from "../../selection_bar/SelectionBar";
import TourGroupsCalendarBar from "../bar/TourGroupsCalendarBar";
import {
    useFetchEntities,
    useGetTourGroupsPaginatedSectioned,
} from "../../../reactQueryHooks";
import TourGroupDateSectionItem from "./TourGroupDateSectionItem";
import { CircularProgress, Button } from "@mui/material";

//----------------------------------------------------------------

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
  position: relative;
`;

const Content = styled.div`
  display: flex;
  justify-content: center;
  padding: 5px;
  overflow: hidden;
  position: relative;
  flex: 1;
`;

const Page = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  padding: 0 5px 0 0;
`;

const ListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  background-color: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(2px);
`;

const LoadMoreButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  width: 100%;
`;

const LoadMoreButton = styled(Button)`
  margin: 0 auto;
`;

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  font-size: 1rem;
  color: #666;
`;

//------------------------------------------------------------------------

const TourGroupsCalendarContent = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Initialize startDate in URL if not present
    useEffect(() => {
        if (!searchParams.get("startDate")) {
            const newParams = new URLSearchParams(searchParams);
            newParams.set("startDate", moment().format("YYYY-MM-DD"));
            setSearchParams(newParams, { replace: true });
        }
    }, []);

    const startDate =
        searchParams.get("startDate") || moment().format("YYYY-MM-DD");
    const endDate = searchParams.get("endDate") || undefined;

    const selectedOptionIds = useMemo(() => {
        const optionIdsParam = searchParams.get("optionIds");
        return optionIdsParam ? optionIdsParam.split(",") : [];
    }, [searchParams]);

    const minSeatsCount = useMemo(() => {
        const minSeatsParam = searchParams.get("minSeats");
        return minSeatsParam ? parseInt(minSeatsParam, 10) : undefined;
    }, [searchParams]);

    const sortBy = (searchParams.get("sortBy") || "date") as "date" | "seats";

    const { data: entitiesData } = useFetchEntities() as {
        data: { data: any };
    };

    const {
        data: tourGroupsData,
        isFetching,
        isLoading,
        hasNextPage,
        fetchNextPage,
        refetch,
        isRefetching,
        isFetchingNextPage,
    } = useGetTourGroupsPaginatedSectioned({
        start_date: startDate,
        end_date: endDate,
        option_ids: selectedOptionIds.length > 0 ? selectedOptionIds : undefined,
        min_seats_count: minSeatsCount,
        sort_by: sortBy,
    }) as any;

    const loading =
        (isFetching || isRefetching || isLoading) && !isFetchingNextPage;

    const tourGroupsSections = useMemo(() => {
        if (!tourGroupsData?.pages) return [];
        return tourGroupsData.pages.flatMap((page: any) => page.data.docs || []);
    }, [tourGroupsData]);

    const handleLoadMore = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    return (
        <Container>
            <SelectionBar>
                <TourGroupsCalendarBar
                    searchParams={searchParams}
                    setSearchParams={setSearchParams}
                    entities={entitiesData?.data}
                />
            </SelectionBar>
            <ContentWrapper>
                <Content
                    style={{
                        position: "relative",
                        flex: 1,
                    }}
                >
                    <Page
                        style={{
                            padding: "0 5px 0 0",
                        }}
                    >
                        {loading && (
                            <LoadingContainer>
                                <CircularProgress />
                            </LoadingContainer>
                        )}
                        <ListContainer>
                            {tourGroupsSections.length === 0 && !loading && (
                                <EmptyState>No tour groups found for the selected criteria.</EmptyState>
                            )}
                            {tourGroupsSections.map((section: any) => (
                                <TourGroupDateSectionItem
                                    key={section.date}
                                    date={section.date}
                                    tourGroups={section.tour_groups}
                                    refetch={refetch}
                                    entities={entitiesData?.data}
                                />
                            ))}
                            {hasNextPage && (
                                <LoadMoreButtonContainer>
                                    <LoadMoreButton
                                        variant="contained"
                                        onClick={handleLoadMore}
                                        disabled={isFetchingNextPage}
                                    >
                                        {isFetchingNextPage ? "Loading..." : "Load More"}
                                    </LoadMoreButton>
                                </LoadMoreButtonContainer>
                            )}
                        </ListContainer>
                    </Page>
                </Content>
            </ContentWrapper>
        </Container>
    );
};

export default TourGroupsCalendarContent;
